/**
 * Part paths: one stable address for every part of a studio draft, so an
 * op, a receipt, a problem, a provenance entry and a selection on the canvas
 * all name the same thing the same way, and two writers can be kept apart by
 * comparing what they touched.
 *
 * The grammar, segments joined by `/`:
 *
 *   meta                                  the workspace's name, currency, language, place
 *   record:<r>                            a whole record or list
 *   record:<r>/props                      its name, entry noun, direction, icon, bindings, capture, layout
 *   record:<r>/order                      its field order
 *   record:<r>/rows                       its seed rows
 *   record:<r>/field:<f>                  a field
 *   record:<r>/field:<lines>/field:<f>    a line field, at most LINES_MAX_DEPTH deep
 *   record:<r>/card:<c>                   one of the record's own cards
 *   board:<k> role:<k> import:<k> note:<k> not_yet:<k>
 *   source:<uuid>                         a source the person brought
 *   gap:<id>                              a gap the person dismissed
 *   app_map:<id>                          the map from the app the person uses (6.14)
 *   *                                     everything (a restore)
 *
 * Keys never change once minted; a rename changes words only.
 */
import type { SetupDraftField } from '../compose-author';
import { STUDIO_LIMITS, type StudioDraft, type StudioRecord } from './draft';

export type StudioPartRef =
  | { kind: 'workspace' }
  | { kind: 'record'; record: string }
  | { kind: 'field'; record: string; within?: string[]; field: string }
  | { kind: 'card'; record: string; card: string }
  | { kind: 'board' | 'role' | 'import' | 'note' | 'not_yet'; key: string }
  | { kind: 'source'; id: string };

/** The path that meets every other. */
export const STUDIO_EVERYTHING = '*';

const KEYED = ['board', 'role', 'import', 'note', 'not_yet'] as const;
type Keyed = (typeof KEYED)[number];
const isKeyed = (value: string): value is Keyed => (KEYED as readonly string[]).includes(value);

export const studioPath = (ref: StudioPartRef): string => {
  switch (ref.kind) {
    case 'workspace':
      return 'meta';
    case 'record':
      return `record:${ref.record}`;
    case 'field':
      return [`record:${ref.record}`, ...(ref.within ?? []).map((key) => `field:${key}`), `field:${ref.field}`].join('/');
    case 'card':
      return `record:${ref.record}/card:${ref.card}`;
    case 'source':
      return `source:${ref.id}`;
    default:
      return `${ref.kind}:${ref.key}`;
  }
};

/** One `kind:key` segment, or null. */
const segment = (raw: string): { kind: string; key: string } | null => {
  const at = raw.indexOf(':');
  if (at <= 0 || at === raw.length - 1) return null;
  return { kind: raw.slice(0, at), key: raw.slice(at + 1) };
};

/**
 * The part a path names. A record's `/props`, `/order` and `/rows` name parts
 * of the record, so they answer the record; `gap:<id>` and `*` are paths, not
 * parts, and answer null, as does anything outside the grammar.
 */
export const studioRefOf = (path: string): StudioPartRef | null => {
  if (path === 'meta') return { kind: 'workspace' };
  const parts = path.split('/');
  const head = segment(parts[0] ?? '');
  if (head === null) return null;
  if (parts.length === 1) {
    if (head.kind === 'record') return { kind: 'record', record: head.key };
    if (head.kind === 'source') return { kind: 'source', id: head.key };
    if (isKeyed(head.kind)) return { kind: head.kind, key: head.key };
    return null;
  }
  if (head.kind !== 'record') return null;
  const rest = parts.slice(1);
  if (rest.length === 1 && (rest[0] === 'props' || rest[0] === 'order' || rest[0] === 'rows')) {
    return { kind: 'record', record: head.key };
  }
  if (rest.length === 1) {
    const card = segment(rest[0]!);
    if (card?.kind === 'card') return { kind: 'card', record: head.key, card: card.key };
  }
  const fields: string[] = [];
  for (const raw of rest) {
    const one = segment(raw);
    if (one === null || one.kind !== 'field') return null;
    fields.push(one.key);
  }
  const field = fields.pop()!;
  return { kind: 'field', record: head.key, ...(fields.length === 0 ? {} : { within: fields }), field };
};

/** The fields a `within` chain lands in, or null when a link of the chain is not a lines field of the one above. */
export const studioFieldsWithin = (record: StudioRecord, within: readonly string[] = []): SetupDraftField[] | null => {
  let fields: SetupDraftField[] = record.fields;
  for (const key of within) {
    const lines = fields.find((field) => field.key === key);
    if (lines === undefined || lines.type !== 'lines' || lines.lines === undefined) return null;
    fields = lines.lines.fields;
  }
  return fields;
};

/** The field a ref names, or null. */
export const studioFieldOf = (draft: StudioDraft, ref: { record: string; within?: string[]; field: string }): SetupDraftField | null => {
  const record = draft.records.find((one) => one.key === ref.record);
  if (record === undefined) return null;
  return studioFieldsWithin(record, ref.within)?.find((field) => field.key === ref.field) ?? null;
};

/** Whether the part a path names is in the draft. `meta` and `*` always are; a gap is when it was dismissed. */
export const studioPartExists = (draft: StudioDraft, path: string): boolean => {
  if (path === STUDIO_EVERYTHING || path === 'meta') return true;
  const head = segment(path.split('/')[0] ?? '');
  if (head?.kind === 'gap' && !path.includes('/')) return draft.dismissedGaps.some((one) => one.gap === head.key);
  if (head?.kind === 'app_map' && !path.includes('/')) return (draft.appMaps ?? []).some((one) => one.id === head.key);
  const ref = studioRefOf(path);
  if (ref === null) return false;
  switch (ref.kind) {
    case 'workspace':
      return true;
    case 'record':
      return draft.records.some((one) => one.key === ref.record);
    case 'field':
      return studioFieldOf(draft, ref) !== null;
    case 'card':
      return draft.records.find((one) => one.key === ref.record)?.tiles?.some((tile) => tile.key === ref.card) ?? false;
    case 'board':
      return draft.boardCards.some((one) => one.key === ref.key);
    case 'role':
      return draft.roles.some((one) => one.key === ref.key);
    case 'import':
      return draft.imports.some((one) => one.key === ref.key);
    case 'note':
      return draft.notes.some((one) => one.key === ref.key);
    case 'not_yet':
      return draft.notYet.some((one) => one.key === ref.key);
    case 'source':
      return draft.sources.some((one) => one.id === ref.id);
  }
};

/**
 * Whether two paths touch the same part: equal, `*` on either side, or one is
 * the other's ancestor. `record:sales/props` and `record:sales/field:amount`
 * are siblings and do not meet; `record:sale` and `record:sales` do not either.
 */
export const studioPathsMeet = (a: string, b: string): boolean =>
  a === b || a === STUDIO_EVERYTHING || b === STUDIO_EVERYTHING || a.startsWith(`${b}/`) || b.startsWith(`${a}/`);

/**
 * The words a part goes by, for a receipt or a question: a record's name, a
 * field's label, a card's label, a Dashboard card's label, a role's name, an
 * import's sheet, a not-yet item's name. Null
 * when the part is gone or has no words of its own.
 */
export const studioPartName = (draft: StudioDraft, path: string): string | null => {
  const ref = studioRefOf(path);
  if (ref === null) return null;
  const recordName = (key: string): string | null => draft.records.find((one) => one.key === key)?.name ?? null;
  switch (ref.kind) {
    case 'workspace':
      return draft.meta.name === '' ? null : draft.meta.name;
    case 'record':
      return recordName(ref.record);
    case 'field':
      return studioFieldOf(draft, ref)?.label ?? null;
    case 'card':
      return draft.records.find((one) => one.key === ref.record)?.tiles?.find((tile) => tile.key === ref.card)?.label ?? null;
    case 'board':
      return draft.boardCards.find((one) => one.key === ref.key)?.label ?? null;
    case 'role':
      return draft.roles.find((one) => one.key === ref.key)?.name ?? null;
    case 'import':
      return draft.imports.find((one) => one.key === ref.key)?.sheet ?? null;
    case 'not_yet':
      return draft.notYet.find((one) => one.key === ref.key)?.name ?? null;
    case 'note':
    case 'source':
      return null;
  }
};

/** The grammar of every key, minted or given: lower case letters, digits and `_`, starting with a letter. */
export const STUDIO_KEY = /^[a-z][a-z0-9_]*$/;

/** The one-letter (or one-word) key a part takes when its words hold nothing a key can be made of. */
export type StudioKeyFallback = 'r' | 'f' | 'l' | 'c' | 'b' | 'role' | 'i' | 'n' | 'x';

/** Keys every line already reads the rows above it by (`SetupDraftLines`), so a field never takes them. */
const RESERVED_FIELD_KEYS: ReadonlySet<string> = new Set(['parent', 'root']);

/**
 * A key for a new part, from the person's words: NFKD with the marks
 * stripped, lower case, every run outside `[a-z0-9]` one `_`, cut to
 * `STUDIO_LIMITS.keyLength`. Words with no Latin letter or digit give the
 * fallback alone; a key that would start with a digit gets the fallback in
 * front; a field (fallback `f`) never takes `parent` or `root`; then `_2`,
 * `_3`… until the key is free. Always `/^[a-z][a-z0-9_]*$/`.
 */
export const mintStudioKey = (words: string, taken: ReadonlySet<string>, fallback: StudioKeyFallback): string => {
  const max = STUDIO_LIMITS.keyLength;
  const trim = (key: string): string => key.replace(/^_+|_+$/g, '');
  let base = trim(
    words
      .normalize('NFKD')
      .replace(/\p{M}+/gu, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_'),
  );
  base = trim(base.slice(0, max));
  if (base === '') base = fallback;
  else if (/^[0-9]/.test(base)) base = trim(`${fallback}_${base}`.slice(0, max));
  const free = (key: string): boolean => !taken.has(key) && !(fallback === 'f' && RESERVED_FIELD_KEYS.has(key));
  if (free(base)) return base;
  for (let n = 2; ; n += 1) {
    const suffix = `_${n}`;
    const key = `${trim(base.slice(0, max - suffix.length))}${suffix}`;
    if (free(key)) return key;
  }
};
